import { View, Text, Pressable, Platform } from "react-native";
import { Link } from "expo-router";
import { Ionicons } from "@expo/vector-icons";

/**
 * Section component for terms content
 */
interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
  <View className="mb-8">
    <Text className="text-xl font-semibold text-gray-900 mb-3">{title}</Text>
    {children}
  </View>
);

/**
 * Paragraph component for consistent text styling
 */
const Paragraph = ({ children }: { children: React.ReactNode }) => (
  <Text className="text-gray-600 leading-7 mb-3">{children}</Text>
);

/**
 * List item component
 */
const ListItem = ({ children }: { children: React.ReactNode }) => (
  <View className="flex-row items-start gap-3 mb-2">
    <View className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
    <Text className="text-gray-600 leading-7 flex-1">{children}</Text>
  </View>
);

/**
 * Terms of Service page for the Staatsen Familie App
 */
export default function TermsPage() {
  // Only render on web platform
  if (Platform.OS !== "web") {
    return null;
  }

  const lastUpdated = "2 januari 2026";

  return (
    <View className="flex-1 min-h-screen">
      {/* Header */}
      <View className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-12">
        <View className="max-w-2xl mx-auto">
          <Link href="../(web)" asChild>
            <Pressable
              className="flex-row items-center gap-2 mb-6"
              style={{ cursor: "pointer" } as any}
            >
              <Ionicons name="arrow-back" size={20} color="white" />
              <Text className="text-white font-medium">Terug</Text>
            </Pressable>
          </Link>

          <View className="flex-row items-center gap-4">
            <View className="w-14 h-14 bg-white/20 rounded-2xl items-center justify-center">
              <Ionicons name="document-text" size={28} color="white" />
            </View>
            <View>
              <Text className="text-2xl md:text-3xl font-bold text-white">
                Gebruiksvoorwaarden
              </Text>
              <Text className="text-blue-200 mt-1">
                Laatst bijgewerkt: {lastUpdated}
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* Content */}
      <View className="flex-1 px-6 py-12">
        <View className="max-w-2xl mx-auto w-full bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <Section title="1. Acceptatie van Voorwaarden">
            <Paragraph>
              Door de Staatsen Familie App te gebruiken, ga je akkoord met deze 
              gebruiksvoorwaarden. Deze app is uitsluitend bedoeld voor leden 
              van de Staatsen familie.
            </Paragraph>
          </Section>

          <Section title="2. Toegang tot de App">
            <Paragraph>
              Toegang tot de app is beperkt tot familieleden die:
            </Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>Een geldige uitnodiging hebben ontvangen</ListItem>
              <ListItem>Het geheime familie-wachtwoord kennen</ListItem>
              <ListItem>Een account hebben aangemaakt met goedkeuring</ListItem>
            </View>
            <Paragraph>
              Het delen van het geheime wachtwoord met personen buiten de 
              familie is niet toegestaan.
            </Paragraph>
          </Section>

          <Section title="3. Accounts voor Kinderen">
            <Paragraph>
              Kinderen zonder eigen telefoon kunnen de app gebruiken via de 
              "Speel als" functie onder toezicht van een ouder of voogd. De 
              verantwoordelijke volwassene:
            </Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>
                Blijft verantwoordelijk voor het gebruik door het kind
              </ListItem>
              <ListItem>
                Zorgt voor passend toezicht tijdens het spelen
              </ListItem>
              <ListItem>
                Kan op elk moment de toegang intrekken
              </ListItem>
            </View>
          </Section>

          <Section title="4. Gedragsregels">
            <Paragraph>
              Als familieleden verwachten we dat iedereen:
            </Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>Respectvol omgaat met elkaar</ListItem>
              <ListItem>Alleen gepaste foto's en berichten deelt</ListItem>
              <ListItem>Eerlijk speelt bij spelletjes</ListItem>
              <ListItem>De privacy van familieleden respecteert</ListItem>
            </View>
          </Section>

          <Section title="5. Inhoud & Foto's">
            <Paragraph>
              Door foto's of andere inhoud te delen in de app:
            </Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>
                Geef je toestemming dat deze zichtbaar zijn voor andere 
                familieleden
              </ListItem>
              <ListItem>
                Bevestig je dat je het recht hebt om de inhoud te delen
              </ListItem>
              <ListItem>
                Begrijp je dat de beheerder ongepaste inhoud kan verwijderen
              </ListItem>
            </View>
          </Section>

          <Section title="6. Spelletjes & Scores">
            <Paragraph>
              De app bevat verschillende spelletjes met scoreboards. Scores 
              worden eerlijk bijgehouden en zijn zichtbaar voor alle 
              familieleden. Valsspelen wordt niet gewaardeerd!
            </Paragraph>
          </Section>

          <Section title="7. App Updates">
            <Paragraph>
              De app kan van tijd tot tijd worden bijgewerkt met nieuwe 
              functies of verbeteringen. We raden aan om altijd de laatste 
              versie te gebruiken voor de beste ervaring.
            </Paragraph>
          </Section>

          <Section title="8. Beëindiging">
            <Paragraph>
              Je kunt op elk moment stoppen met het gebruik van de app en je 
              account verwijderen. De beheerder behoudt het recht om accounts 
              te verwijderen bij misbruik of bij het niet naleven van deze 
              voorwaarden.
            </Paragraph>
          </Section>

          <Section title="9. Aansprakelijkheid">
            <Paragraph>
              De app wordt aangeboden "zoals deze is" voor familieplezier. 
              We doen ons best om de app werkend en veilig te houden, maar 
              kunnen geen garanties geven. De app is niet bedoeld voor 
              commercieel gebruik.
            </Paragraph>
          </Section>

          <Section title="10. Wijzigingen">
            <Paragraph>
              Deze voorwaarden kunnen worden aangepast. Bij belangrijke 
              wijzigingen zullen familieleden worden geïnformeerd via de app.
            </Paragraph>
          </Section>

          <Section title="11. Contact">
            <Paragraph>
              Vragen over deze voorwaarden? Neem contact op met de 
              familie-beheerder of stuur een e-mail naar:{" "}
              <Text className="text-blue-600 font-medium">
                support@staatsen.family
              </Text>
            </Paragraph>
          </Section>

          {/* Bottom note */}
          <View className="mt-8 p-4 bg-blue-50 rounded-xl">
            <Text className="text-sm text-blue-700 text-center">
              Door de app te gebruiken, bevestig je dat je deze voorwaarden 
              hebt gelezen en geaccepteerd.
            </Text>
          </View>
        </View>
      </View>

      {/* Footer */}
      <View className="bg-gray-50 border-t border-gray-200 px-6 py-8">
        <View className="max-w-2xl mx-auto items-center">
          <Text className="text-sm text-gray-500 text-center">
            © {new Date().getFullYear()} Staatsen Familie App
          </Text>
        </View>
      </View>
    </View>
  );
}

